import "./OtaDeleteFolderModal.scss";
import { useState, useEffect } from "react";
import { Modal, Input, Alert, Spin, Typography, notification } from "antd";
import { WarningFilled } from "@ant-design/icons";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import { formatDate } from '../../utils/dates';

const { Text } = Typography;

/**
 * Confirmación de borrado, tanto de una carpeta —firmware, canal o variante— como de
 * una versión suelta.
 *
 * En los dos casos la operación no tiene vuelta atrás, así que antes de permitirla se
 * dice exactamente qué se pierde y se exige escribirlo: la ruta completa al borrar una
 * carpeta, el número de versión al borrar una versión. La idea no es poner trabas, es
 * que nadie lo ejecute por inercia.
 *
 * @param {String} path Ruta de la carpeta, o de la carpeta que contiene la versión
 * @param {String} kind firmware, channel o variant
 * @param {Object} version Versión a borrar, si se está borrando una: { version, is_latest, file }
 */
function OtaDeleteFolderModal({ open, path, kind, version = null, onCancel, onDeleted, lang }) {

  // ===== 1. ESTADOS =====
  const [confirmation, setConfirmation] = useState('');
  const [contents, setContents] = useState(null);
  const [ui, setUI] = useState({ loading: false, deleting: false });

  // ===== 2. DERIVADOS =====
  // Lo que hay que escribir: la versión es corta y basta con ella; una carpeta arrastra
  // todo lo que cuelga, así que se pide la ruta entera.
  const expected = version ? version.version : path;
  const confirmed = confirmation.trim() === expected;

  const kindLabel = {
    firmware: lang.handle('el firmware'),
    channel: lang.handle('el canal'),
    variant: lang.handle('la variante'),
  }[kind] ?? lang.handle('la carpeta');

  const title = version ? lang.handle('la versión') : kindLabel;

  // ===== 3. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));

  const deleteUrl = () => {
    if (!version) return `ota/folders?path=${encodeURIComponent(path)}`;

    const [product, channel, ...target] = path.split('/');
    const query = new URLSearchParams();
    if (target.length) query.set('target', target.join('/'));
    // Borrar la que está en distribución arrastra el puntero del canal, OTA exige confirmarlo
    if (version.is_latest) query.set('force', 'true');

    const suffix = query.toString() ? `?${query.toString()}` : '';
    return `ota/versions/${product}/${channel}/${version.version}${suffix}`;
  };

  // ===== 4. HANDLERS =====
  const closeModal = () => {
    if (ui.deleting) return;

    setConfirmation('');
    setContents(null);
    onCancel();
  };

  const submit = async () => {
    if (!confirmed) return;

    updateUI('deleting', true);
    try {
      const res = await service.del(deleteUrl());
      if (!res) return;

      if (res.data.ok === false) {
        notification["error"]({ message: lang.handle(res.data.message) });
        return;
      }

      notification["success"]({
        message: `${lang.handle('Eliminado')}: ${version ? `${path}/${version.version}` : path}`
      });

      setConfirmation('');
      setContents(null);
      onDeleted();

    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      updateUI('deleting', false);
    }
  };

  // ===== 5. EFFECTS =====
  useEffect(() => {
    if (!open || !path) return;

    setConfirmation('');
    setContents(null);

    // De una versión ya se sabe todo por el listado; de una carpeta hay que ir a contar
    if (version) return;

    updateUI('loading', true);

    service.get("ota/folders", { path })
      .then(res => {
        if (res && res.data.ok !== false) setContents(res.data.data);
      })
      .catch(err => console.error("Error devuelto: " + err))
      .finally(() => updateUI('loading', false));
  }, [open, path, version]);

  // ===== 6. RENDER =====
  return (
    <Modal
      title={<span className="ota-delete__title"><WarningFilled /> {lang.handle('Eliminar')} {title}</span>}
      open={open}
      onCancel={closeModal}
      onOk={submit}
      okText={lang.handle('Eliminar definitivamente')}
      cancelText={lang.handle('Cancelar')}
      okButtonProps={{ danger: true, disabled: !confirmed || ui.loading }}
      cancelButtonProps={{ disabled: ui.deleting }}
      confirmLoading={ui.deleting}
      maskClosable={!ui.deleting}
      keyboard={!ui.deleting}
      className="ota-delete"
      destroyOnClose
    >
      <Alert
        type="error"
        showIcon
        className="ota-delete__alert"
        message={lang.handle('Esta acción no se puede deshacer')}
        description={version
          ? version.is_latest
            ? lang.handle('Es la versión en distribución: al borrarla se retira también el puntero del canal y los equipos dejarán de recibir actualizaciones desde aquí.')
            : lang.handle('Se eliminará el paquete y su manifest. Los equipos que ya la tengan instalada no se ven afectados.')
          : lang.handle('Se eliminarán todas las versiones que cuelgan de esta ruta, incluidas las que estén en distribución. Los equipos dejarán de recibir actualizaciones desde aquí.')
        }
      />

      <div className="ota-delete__path">
        <span className="ota-delete__path-label">{lang.handle('Se eliminará')}</span>
        <Text code className="ota-delete__path-value">
          {version ? `${path}/${version.version}` : path}
        </Text>
      </div>

      {version
        ? <ul className="ota-delete__summary">
          {version.release_date &&
            <li>
              <strong>{formatDate(version.release_date)}</strong> {lang.handle('publicada')}
            </li>
          }
          {version.release_by &&
            <li>
              <strong>{version.release_by}</strong>
            </li>
          }
        </ul>
        : ui.loading
          ? <div className="ota-delete__loading"><Spin size="small" /> <Text type="secondary">{lang.handle('Calculando lo que contiene')}</Text></div>
          : contents &&
          <ul className="ota-delete__summary">
            <li>
              <strong>{contents.versions}</strong> {contents.versions === 1 ? lang.handle('versión') : lang.handle('versiones')}
            </li>
            {contents.folders > 0 &&
              <li>
                <strong>{contents.folders}</strong> {contents.folders === 1 ? lang.handle('carpeta') : lang.handle('carpetas')}
              </li>
            }
          </ul>
      }

      <label className="ota-delete__confirm">
        <span className="ota-delete__confirm-label">
          {version
            ? <>{lang.handle('Escribe la versión para confirmar')}: <Text code>{expected}</Text></>
            : <>{lang.handle('Escribe la ruta para confirmar')}: <Text code>{expected}</Text></>
          }
        </span>
        <Input
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          onPressEnter={() => confirmed && submit()}
          placeholder={expected}
          disabled={ui.deleting}
          status={confirmation && !confirmed ? 'error' : undefined}
          autoComplete="off"
        />
      </label>
    </Modal>
  );
}

export default withLocale(OtaDeleteFolderModal);
